<template>
  <div>
    <!-- Main Dialog -->
    <v-dialog :value="value" fullscreen persistent no-click-animation>
      <v-card>
        <v-card-title ref="cardTitle">
          <span class="headline">ドローン画像アップロード</span>
          <v-spacer></v-spacer>
          <v-btn depressed color="white" fab>
            <v-icon v-on:click="close" size="30">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols>
              <div ref="previewMap"></div>
            </v-col>
            <v-col cols="auto" style="width: 480px">
              <v-row>
                <span class="text-caption">* は必須項目です。</span>
                <span class="text-caption">サーモ画像と可視画像を両方持っている場合は、両方アップロードしてください。</span>
                <span class="text-caption">M300（H20T）はサーモ画像とWIDE画像のみアップロードしてください。</span>
                <span class="text-caption">※ ZOOM画像は別途 Google ドライブ「S.O&M_ドローン画像」に保存してください。</span>
              </v-row>
              <v-form
                  ref="droneImageUploadForm"
              >
                <v-row class="div-images-upload">
                  <v-col >
                    <p>利用区分 *</p>
                    <v-chip
                        v-for="item in useTypeItems"
                        :key="item.value"
                        @click="use_type = item.value;"
                        :color="use_type == item.value ? item.color : 'default'"
                    >
                      {{item.text}}
                    </v-chip>
                  </v-col>
                </v-row>
                <v-row class="div-images-upload">
                  <v-col cols="auto">
                    <v-btn
                        color="primary"
                        outlined
                        @click="onAddImages"
                        :disabled="uploadBtnDisable"
                    >
                      画像追加
                    </v-btn>
                  </v-col>
                  <v-col>
                    <h4 style="padding-top: 7px">
                      サーモ数：{{ coordinate['thermal'].length }}
                    </h4>
                    <h4 style="padding-top: 7px">
                      可視数：{{ coordinate['color'].length }}
                    </h4>
                    <v-divider></v-divider>
                    <h4 style="padding-top: 7px">
                      画像数：{{ inputImagesUpload.images.length }}
                    </h4>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-checkbox
                      class="mt-0 pt-0"
                      v-model="checkRTK"
                      label="RTKをチェック"
                      hide-details
                      @click="confirmCheckRTK"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                        v-model="note"
                        label="備考"
                        outlined
                        clearable
                        dense
                        hide-details
                        class="mb-0"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row v-show="coordinate['thermal'].length > 0">
                  <v-col cols="12">
                    <v-checkbox
                        class="mt-0 pt-0"
                        v-model="sendAirprobe"
                        label="AI MODEL A（有料）異常検知を実行"
                        hide-details
                        @click="confirmSendAirprobe"
                    ></v-checkbox>
                    <airprobe-required-data-dialog
                        v-model="requiredDataDialog"
                        :plantId="plantId"
                        @sendAirprobeRequest="sendAirprobeRequest"
                        @dcUpdated="updateDC"
                        @cancelUpdateDC="cancelUpdateDC"
                    ></airprobe-required-data-dialog>
                  </v-col>
                </v-row>
              </v-form>
              <v-card-actions ref="cardActions">
                <v-spacer></v-spacer>
                <v-btn
                    depressed
                    @click.native="close()"
                >
                  キャンセル
                </v-btn>
                <v-btn
                    depressed
                    color="primary"
                    @click="uploadImages"
                    :disabled="uploadBtnDisable"
                >
                  アップロード
                </v-btn>
              </v-card-actions>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Invisible File Input -->
    <input
      ref="imagesUploader"
      class="d-none"
      type="file"
      multiple
      accept="image/*"
      @change="addImages"
      @click="$refs.imagesUploader.value = null"
    />

    <!-- Show Error Element -->
    <v-snackbar v-model="snackbar" top left timeout="-1">
      <div>アップロードできない下記のファイルを</div>
      <div>再度アップロードしてください。</div>
      <h4 v-for="(item, i) in errorFileNames" :key="i">{{ item }}</h4>
      <template v-slot:action="{ attrs }">
        <v-btn color="pink" text v-bind="attrs" @click="snackbar = false"
          >閉じる</v-btn
        >
      </template>
    </v-snackbar>

    <!-- Uploading Overlay -->
    <upload-progress-dialog
        :uploadFilesNum="uploadFilesNum"
        :uploadedFilesNum="uploadedFilesNum"
        :uploadingDialog="uploadingDialog"
        :loading="loading"></upload-progress-dialog>
  </div>
</template>
<script>
import {store} from "@/store";
import Map from "ol/Map";
import LayerGroup from "ol/layer/Group";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import View from "ol/View";
import ExifReader from 'exifreader';
import {transform} from 'ol/proj';
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import {Circle, Fill, Stroke, Style, Text} from "ol/style";
import TileState from "ol/TileState";
import AirprobeRequiredDataDialog from "@/components/dialog/AirprobeRequiredDataDialog";
import UploadProgressDialog from "@/components/dialog/UploadProgressDialog";
import S3Axios from "@/utils/s3Axios";

export default {
  name: "DroneImagesUploadDialog",

  props: ["value", "plantId", "folderId"],
  components: {
    UploadProgressDialog,
    'airprobe-required-data-dialog': AirprobeRequiredDataDialog
  },

  data() {
    return {
      loading: "done",
      uploadingDialog: false,
      snackbar: false,
      uploadFolderId: this.folderId,
      inputImagesUpload: {
        images: [],
      },
      errorFileNames: [],
      uploadedFilesNum: 0,
      uploadFilesNum: 0,
      useTypeItems: [
        { text: "工事", value: "1construction", color: "purple lighten-1" },
        { text: "査定", value: "2assessment", color: "green lighten-1" },
        { text: "メンテ", value: "3maintenance", color: "orange lighten-1" },
        { text: "ドローン", value: "4drone", color: "light-blue accent-4" },
      ],
      use_type: "",
      map: null,
      fileCoordinates: [],
      uploadBtnDisable: false,
      note: null,
      coordinate: {
        'color': [],
        'thermal': [],
      },
      sendAirprobe: false,
      requiredDataDialog: false,
      sendAirprobeRequest: () => {},

      uploadImageColorType: [],
      folder: null,
      checkRTK: false,
      selectedImagesLength: 0,
      imagesUpload: [],
      s3Axios: new S3Axios(this.axios).s3Axios,
    };
  },

  methods: {
    createFolder() {
      const body = {
        plant_id: this.plantId,
        type: 'map',
        use_type: this.use_type,
        property: {
          shooting_date: null,
          drone_type: null,
          shooting_height: null,
          gimbal_angle: null,
          gcp: null,
          weather: null,
          solar_radiation: null,
          rtk: null,
        },
        upload_image_color_type: this.uploadImageColorType,
        coordinate: this.coordinate,
        note: this.note,
        sendAirprobe: this.sendAirprobe,
      };
      return this.axios.post("/api/folder", body);
    },
    onAddImages() {
      this.$refs.imagesUploader.click();
    },
    checkDoneLoadingImages(errorFile){
      if (errorFile.length + this.inputImagesUpload.images.length === this.selectedImagesLength){
        this.fileCoordinates = JSON.parse(JSON.stringify(this.fileCoordinates));
        this.setSourcePreviewMap();
        this.uploadBtnDisable = false;
        if (this.coordinate.thermal.length > 0 && !this.uploadImageColorType.includes('thermal')){
          this.uploadImageColorType.push('thermal');
        }
        if (this.coordinate.color.length > 0 && !this.uploadImageColorType.includes('color')){
          this.uploadImageColorType.push('color');
        }
      }
    },
    addImages(e) {
      if (!e.target.files || e.target.files.length === 0) return;
      this.uploadBtnDisable = true;
      const errorFile = [];
      for (const file of e.target.files) {
        // 画像が重複じゃないと画像一覧に追加する。
        if (
          !this.inputImagesUpload.images.some(
            (x) => x.name === file.name && x.size === file.size
          )
        ) {
          this.selectedImagesLength++;
          const filename = file.name;
          if (filename.split('.')[0].match(/^[\w()-]+$/)){
            const reader = new FileReader();
            reader.onload = (readerEvent) => {
              try {
                const tags = ExifReader.load(readerEvent.target.result, {expanded: true});
                const lat = tags.gps.Latitude;
                const lng = tags.gps.Longitude;
                const location = [lng, lat];
                this.fileCoordinates.push({coordinates: location, file_name: filename, type: this.getColorType(tags)});
                this.coordinate[this.getColorType(tags)].push({filename: filename, latitude: location[1], longitude: location[0]});
                this.inputImagesUpload.images.push(file);
                this.imagesUpload.push({name: file.name, color_type: this.getColorType(tags)});
                this.checkDoneLoadingImages(errorFile);
              } catch (error) {
                store.showErrorDialog('画像情報の読み取りができません。システム管理者にご連絡ください。', () => {
                  this.close();
                });
              }
            };
            reader.readAsArrayBuffer(file);
          }
          else {
            errorFile.push(filename);
          }
        }
      }
      this.checkDoneLoadingImages(errorFile);
      if (errorFile.length > 0){
        store.showErrorDialog('以下のファイルにファイル名に指定できない文字が使用されています。<br>' +
            '※可能:半角英数字、小かっこ「(」・「)」、ハイフン「–」、アンダースコア「_」<br>' +
            '※不可:全角、日本語、スペース、上記以外の記号など<br>'
            + errorFile.join('<br>'), () => {
          this.uploadBtnDisable = false;
        });
      }
    },
    getColorType(tags){
      if (tags.file['Image Width'].value < 1000){
        return 'thermal';
      }
      return 'color';
    },
    setSourcePreviewMap(){
      const features = this.fileCoordinates.map((item) => {
        return {
          type: 'Feature',
          id: item.file_name,
          geometry: {
            type: "Point",
            coordinates: transform(item.coordinates, 'EPSG:4326', 'EPSG:3857'),
          },
          properties: {
            type: item.type,
            text: item.file_name,
          },
        }
      });
      const rawSource = {
        type: "FeatureCollection",
        features: features,
      };
      const source = new VectorSource({
        features: new GeoJSON().readFeatures(rawSource),
      });
      this.map.getLayers().getArray()[1].setSource(source);
      const extent = this.calculateExtent();
      this.map.getView().animate({
        center: [(extent[2]+extent[0])/2, (extent[3]+extent[1])/2],
        zoom: 17,
        duration: 2000,
      }, () => this.setSatelliteLayer(extent));
      let selected = null;
      this.map.on('pointermove', (e) => {
        if (selected !== null){
          selected.setStyle(this.featureStyleFunction);
          selected = null;
        }
        this.map.forEachFeatureAtPixel(e.pixel, (f) => {
          const color = this.getColorByType(f.get("type"));
          selected = f;
          selected.setStyle(
            new Style({
              image: new Circle({
                radius: 4,
                fill: new Fill({ color: color }),
                stroke: new Stroke({ color: "#000", width: 3 }),
              }),
              text: new Text({
                font: '14px Calibri,sans-serif',
                offsetY: 19,
                fill: new Fill({
                  color: '#fff',
                  width: 10,
                }),
                stroke: new Stroke({
                  color: '#000',
                  width: 3,
                }),
                text: f.get("text")
              }),
            })
          );
          return true;
        });
      });
    },
    calculateExtent(){
      const lngArray = this.fileCoordinates.map((o) => {return o.coordinates[0]});
      const latArray = this.fileCoordinates.map((o) => {return o.coordinates[1]});
      let maxLng = Math.max.apply(Math, lngArray);
      let maxLat = Math.max.apply(Math, latArray);
      let minLng = Math.min.apply(Math, lngArray);
      let minLat = Math.min.apply(Math, latArray);

      [maxLng, maxLat] = transform([maxLng, maxLat], 'EPSG:4326', 'EPSG:3857');
      [minLng, minLat] = transform([minLng, minLat], 'EPSG:4326', 'EPSG:3857');
      return [minLng-100, minLat-100, maxLng+100, maxLat+100];
    },
    async uploadImages() {
      if (!this.$refs.droneImageUploadForm.validate()||!this.use_type) {
        store.showErrorDialog(
          "利用区分を選択してください。"
        );
        return;
      }
      if (
        !this.inputImagesUpload.images ||
        this.inputImagesUpload.images.length === 0
      ) {
        store.showErrorDialog(
          "「画像追加」をクリックし、アップロードしたい画像を追加してください。"
        );
        return;
      }


      if (!this.folderId) {
        try {
          const response = await this.createFolder();
          this.uploadFolderId = response.data.id;
          this.folder = response.data;
        } catch (error) {
          store.showErrorDialog("エラーが発生しました。管理者に連絡してください。");
          return;
        }
      } else {
        this.uploadFolderId = this.folderId;
        await this.axios.get('/api/folder/' + this.folderId).then((response) => {
          this.folder = response.data.folder;
        })
      }

      this.snackbar = false;
      this.uploadedFilesNum = 0;
      this.uploadFilesNum = this.inputImagesUpload.images.length;
      this.loading = "uploading";
      this.uploadingDialog = true;
      const uploadStatus = new Array(this.inputImagesUpload.images.length); //Value: color, thermal, error
      const success_upload_list = [];
      const fail_upload_list = [];

      let presigned_urls = [];

      // アップロードURLを取得
      await this.axios.post("/api/folder/presigned-urls/" + this.uploadFolderId, {images: this.imagesUpload})
          .then(response => {
            presigned_urls = response.data;
          })
          .catch(() => {
            store.showErrorDialog("エラーが発生しました。再度アップロードしてください。");
            this.loading = "done";
            this.uploadingDialog = false;
            this.close();
          });

      // アップロード
      if (presigned_urls.length == this.inputImagesUpload.images.length){
        for (let k = 0; k < this.inputImagesUpload.images.length; k++) {
          const presigned_url = presigned_urls[k];
          this.s3Axios
              .put(presigned_url, this.inputImagesUpload.images[k])
              .then(() => {
                uploadStatus[k] = this.fileCoordinates[k].type;
                success_upload_list.push(this.inputImagesUpload.images[k].name);
              })
              .catch(() => {
                uploadStatus[k] = "error";
                fail_upload_list.push(this.inputImagesUpload.images[k].name);
              })
              .then(() => {
                this.uploadedFilesNum = uploadStatus.filter(
                    (x) => x != undefined
                ).length;
                if (!uploadStatus.includes(undefined)) {
                  if (!uploadStatus.includes("error")) {
                    //全ての画像がアップロード済なら完了APIを呼び出す。
                    this.axios
                        .post('/api/folder/upload/success/' + this.uploadFolderId, {successFiles: success_upload_list, failFiles: fail_upload_list})
                        .then(async () => {
                          this.loading = "completing";
                          await this.updateFolderProperty(this.inputImagesUpload.images[0]);
                          this.completeUploadImages(this.fileCoordinates);
                        })
                        .catch(() => {
                          this.loading = "done";
                          this.uploadingDialog = false;
                          store.showErrorDialog('エラーが発生しました。<br>もう一度やり直してください。',
                              () => {
                                this.$emit('uploaded');
                                this.close();
                              })
                        })
                  }
                  else {
                    //アップロードできない画像があればアップロードできない画像一覧を表示する。
                    this.axios
                        .post('/api/folder/upload/fail/' + this.uploadFolderId, {successFiles: success_upload_list, failFiles: fail_upload_list, checkRTK: this.checkRTK})
                        .then(() => {
                          const errorIndexs = uploadStatus
                              .map((_, i) => i)
                              .filter((j) => uploadStatus[j] === "error");
                          this.errorFileNames = errorIndexs.map(
                              (x, i) => i + 1 + ". " + this.inputImagesUpload.images[x].name
                          );
                          const errorMsg =
                              "アップロード失敗しました。再アップロード画面で確認して、再度アップロードしてください。";
                          store.showErrorDialog(errorMsg, () => {
                            this.$router.push(`/folder/reupload/${this.uploadFolderId}`);
                          });
                          this.snackbar = true;
                          this.loading = "done";
                          this.uploadingDialog = false;
                        })
                        .catch(() => {
                          this.loading = "done";
                          this.uploadingDialog = false;
                          store.showErrorDialog('エラーが発生しました。<br>もう一度やり直してください。', () => {
                            this.$emit('uploaded');
                            this.close();
                          })
                        });
                  }
                }
              });
        }
      }
      else {
        store.showErrorDialog("エラーが発生しました。再度試してください。");
        this.loading = "done";
        this.uploadingDialog = false;
      }
    },
    async updateFolderProperty(file) {
      const data = new FormData();
      data.append("file", file);
      data.append("type", 'image');
      data.append("rtk", this.checkRTK);
      await this.axios
          .post("/api/folder/property/" + this.uploadFolderId, data);
    },
    getCompletedTypes(uploadStatus) {
      const result = [];
      if (uploadStatus.includes("color")) result.push("color");
      if (uploadStatus.includes("thermal")) result.push("thermal");
      return result;
    },
    completeUploadImages(data) {
      const completedTypes = data.map((item) => {
        return item.type;
      });
      const body = {
        completedTypes: this.getCompletedTypes(completedTypes),
        reupload: false
      };
      this.axios
          .post("/api/folder/images/complete/" + this.uploadFolderId, body)
          .then(() => {
            store.showSuccessDialog("アップロードが完了しました。");
          })
          .catch(() => {
            store.showErrorDialog(
                "アップロードできましたが、アップロード後の処理にてエラーが発生しました。",
                () => {
                  if (this.$route.name !== 'Folder'){
                    this.$router.push(`/plant/${this.folder.plant_id}/folder`);
                  }
                  else {
                    this.$emit("uploaded", "map");
                  }
                }
            );
          })
          .then(() => {
            this.loading = "done";
            this.uploadingDialog = false;
            this.$emit("uploaded", "map");
            this.close();
          });
    },
    close() {
      this.inputImagesUpload.images = [];
      this.use_type = '';
      this.$emit("input");
      this.$refs.droneImageUploadForm.reset();
      this.fileCoordinates = [];
      this.checkRTK = false;
    },
    initMap(){
      const a = window.innerHeight - this.$refs.cardTitle.offsetHeight - 24;
      this.$refs.previewMap.setAttribute('style', "height: " + a + "px");
      this.map = new Map({
        target: this.$refs.previewMap,
        layers:[
          new LayerGroup({
            title: 'BaseLayer',
            layers: [
              new TileLayer({
                zIndex: 0,
                title: "OpenStreetMap",
                source: new OSM(),
              }),
            ]
          }),
          new VectorLayer({
            title: 'VectorLayer',
            zIndex: 2,
            visible: true,
            source: new VectorSource(),
            style: this.featureStyleFunction,
          })
        ],
        view: new View({
          zoom: 5,
          center: [15550453.550049, 4257999.099720],
        })
      });
    },
    featureStyleFunction(feature){
      const color = this.getColorByType(feature.get("type"));
      return new Style({
        image: new Circle({
          radius: 4,
          fill: new Fill({ color: color }),
          stroke: new Stroke({ color: "#000", width: 1 }),
        }),
      });
    },
    getColorByType(type){
      if (type == 'color') return 'rgba(76, 175, 80, 0.7)';
      return 'rgba(255, 152, 0, 0.7)';
    },
    getLayerByTitle(title, fromLayer){
      const layers = fromLayer.getLayers().getArray();
      for (const layer of layers) {
        if (layer.get("title") == title){
          return layer;
        }
        else{
          if (layer instanceof LayerGroup){
            const a = this.getLayerByTitle(title, layer);
            if (a) return a;
          }
        }
      }
      return null;
    },
    setSatelliteLayer(extent){
      const satellite = this.getLayerByTitle('Satellite', this.map);
      if (!satellite){
        const baseLayer = this.getLayerByTitle('BaseLayer', this.map);
        if (baseLayer){
          baseLayer.getLayers().getArray().push(new TileLayer({
            title: "Satellite",
            zIndex: 1,
            source: new OSM({
              minZoom: 17,
              maxZoom: 17,
              url: '/api/tiles/{z}/{x}/{y}',
              tileLoadFunction: (tile, src) => {
                const xhr = new XMLHttpRequest();
                xhr.responseType = 'blob';
                xhr.addEventListener('loadend', function () {
                  const data = this.response;
                  if (data !== undefined) {
                    tile.getImage().src = URL.createObjectURL(data);
                  } else {
                    tile.setState(TileState.ERROR);
                  }
                });
                xhr.addEventListener('error', function () {
                  tile.setState(TileState.ERROR);
                });
                xhr.open('GET', src);
                xhr.setRequestHeader('Authorization', `Bearer ${store.state.token}`);
                xhr.send();
              }
            }),
          }));
        }
      }
      const screenExtent = this.map.getView().calculateExtent();
      const mapExtent = [
        extent[0] - 1000,
        extent[1] - 1000,
        extent[2] + 1000,
        extent[3] + 1000,
      ];
      mapExtent.forEach((position, index) => {
        if (position < screenExtent[index] && index >= 2) {
          mapExtent[index] = screenExtent[index];
        }
        if (position > screenExtent[index] && index <= 1) {
          mapExtent[index] = screenExtent[index];
        }
      });
      this.map.setView(new View({
        maxZoom: 23,
        zoom: 17,
        center: this.map.getView().getCenter(),
        extent: mapExtent,
      }));
      this.map
          .getView()
          .fit(extent);
    },
    confirmSendAirprobe(){
      if (this.sendAirprobe){
        const confirmDialog = {
          title: 'AI MODEL A異常検知を実行確認',
          content: "AI MODEL A異常検知を実行します。よろしいですか。",
          onAgree: () => {
            this.sendAirprobe = true;
            this.axios
                .get('/api/airprobe/check-required-data/' + this.plantId)
                .then((response) => {
                  const property = response.data;
                  if (!property.dc){
                    // this.sendAirprobeRequest = () => {this.completeUploadImages(this.fileCoordinates, image_data_urls);}
                    this.requiredDataDialog = true;
                  }
                  else{
                    // this.completeUploadImages(this.fileCoordinates, image_data_urls);
                  }
                });
          },
          onDisagree: () => {this.sendAirprobe = false},
        };
        store.showConfirmDialog(confirmDialog);
      }
    },
    updateDC(){
      this.requiredDataDialog = false;
    },
    cancelUpdateDC(){
      this.sendAirprobe = false;
    },
    confirmCheckRTK() {
      this.checkRTK = true;
    },
  },

  mounted() {
    this.initMap();
  },
};
</script>

<style lang="scss">
.div-images-upload {
  border-bottom: 1px solid #ccc;
}
.v-btn-toggle--group > .v-btn.v-btn {
  background-color: #f5f5f5!important;
  color: #1976d2;
  font-weight: bold;
}
.v-chip{
  margin: 5px;
}
.div-images-upload p {
  margin-bottom: 0px;
}

.v-card .v-card__actions{
  padding-top: 8px;
}
</style>
